// TypeScript port of zagentc/src/mapping_engine.c's rule engine, used only
// by the Mapping edit screen's client-side preview (see mappingPreview.ts).
//
// Why a port instead of a shared implementation: the real engine runs in the
// collector (zagentc), on the customer's own infra, against data it fetched
// itself - see docs/METRICS_AND_DATA_SOURCES_PLAN.md §6 ("the mapping runs
// client-side... the backend stays a thin signal store; spec is stored and
// returned verbatim and never interpreted server-side"). Adding a preview
// endpoint that fetches the customer's source *from the backend* would reopen
// the SSRF surface that decision was made to avoid (§9), and adding a Go
// duplicate of the engine to interpret `spec` server-side would break that
// same invariant even without a network fetch. So preview instead runs here,
// in the browser, against a Record the user pastes/uploads - never touching
// the backend at all. Keep this in sync with mapping_engine.c's semantics by
// hand; there's no way to share the C source with the frontend build.
//
// A deliberately close mirror of the C file's structure and field names
// (rule.to/from/const/default/cast/unitScale/timestamp/map/required) so the
// two can be diffed against each other when one changes.

export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

// One step of a rule's `ops` chain: an arithmetic operator and its operand - a
// constant (`value`) or another field of the same record (`from`, which wins
// when both are given).
export type MappingOpSymbol = '+' | '-' | '*' | '/';
export interface MappingOp {
  op: MappingOpSymbol | string;
  value?: number;
  from?: string;
}

export interface MappingRule {
  to?: string;
  from?: string;
  const?: JsonValue;
  default?: JsonValue;
  cast?: string;
  unitScale?: number;
  timestamp?: string;
  map?: Record<string, JsonValue>;
  required?: boolean;
  // Arithmetic chain run after cast and unitScale - see applyOps.
  ops?: MappingOp[];
}

export interface MappingOutputSpec {
  rules?: MappingRule[];
  // Scraper/webhook pipeline (backend genericSignals): the list of records THIS
  // output reads from the document (one document, many metrics), keeping only
  // the newest `last` of them; `calc` is this output's Output Value block.
  recordPath?: string;
  last?: number;
  calc?: Record<string, unknown>;
}

export type AggregateOp = 'count' | 'sum' | 'avg' | 'min' | 'max' | 'first' | 'last';
export const AGGREGATE_OPS: AggregateOp[] = ['count', 'sum', 'avg', 'min', 'max', 'first', 'last'];

export interface AggregateSpec {
  field?: string;
  op: AggregateOp;
  as?: string;
}

export interface SortSpec {
  field: string;
  order?: 'asc' | 'desc';
}

// Reshapes the whole record list before the rules run - group/aggregate ->
// sort -> limit -> select (mirrors mapping_engine.c's mapping_engine_transform;
// keep the two in step).
export interface TransformSpec {
  groupBy?: string[];
  aggregate?: AggregateSpec[];
  // A bare string is shorthand for {field, order: 'asc'}.
  sortBy?: (string | SortSpec)[];
  limit?: number;
  select?: string[];
}

export interface MappingSpec {
  transform?: TransformSpec;
  rules?: MappingRule[];
  outputs?: MappingOutputSpec[];
  staticLabels?: Record<string, JsonValue>;
  metricAllowlist?: string[];
  recordPath?: string;
  [key: string]: unknown;
}

// --- path resolution (mirrors mapping_engine_resolve_path) --------------

export function resolvePath(root: JsonValue | undefined, path: string | undefined): JsonValue | undefined {
  if (!path) return root;
  let cur: JsonValue | undefined = root;
  // A key that itself contains dots (what transform.select / aggregate.as
  // produce, e.g. "labels.mount") is addressable by its whole path first.
  if (root && typeof root === 'object' && !Array.isArray(root) && path.includes('.') && path in (root as object)) {
    return (root as Record<string, JsonValue>)[path];
  }
  for (const seg of path.split('.')) {
    if (cur == null) return undefined;
    if (Array.isArray(cur)) {
      if (!/^\d+$/.test(seg)) return undefined;
      cur = cur[Number(seg)];
    } else if (typeof cur === 'object') {
      cur = (cur as Record<string, JsonValue>)[seg];
    } else {
      return undefined;
    }
  }
  return cur;
}

// --- scalar <-> text (mirrors json_value_to_text) ------------------------

// printf("%.10g", n) - fixed or exponential depending on magnitude, trailing
// zeros stripped. Only needs to be *close* to the C output: this is a
// preview, not a wire-format guarantee.
export function formatG(n: number, precision = 10): string {
  if (!Number.isFinite(n)) return String(n);
  if (n === 0) return '0';
  const exp = Math.floor(Math.log10(Math.abs(n)));
  let s: string;
  if (exp < -4 || exp >= precision) {
    s = n.toExponential(precision - 1).replace(/\.?0+e/, 'e');
  } else {
    const decimals = Math.max(0, precision - 1 - exp);
    s = n.toFixed(decimals);
    if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
  }
  return s;
}

function valueToText(v: JsonValue | undefined): string | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return formatG(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return undefined; // array/object - "not resolved", same as a miss
}

// --- fixed transform set (mirrors apply_cast/apply_unit_scale/apply_map_values) --

function applyCast(text: string, cast: string | undefined): string {
  if (cast === 'number') {
    const v = parseFloat(text);
    return Number.isNaN(v) ? text : formatG(v);
  }
  if (cast === 'bool') {
    const falsy = text === '' || text === 'false' || text === '0' || text === 'no';
    return falsy ? 'false' : 'true';
  }
  return text; // "string" (or unrecognised): no-op
}

function applyUnitScale(text: string, scale: number | undefined): string {
  if (typeof scale !== 'number') return text;
  const v = parseFloat(text);
  return Number.isNaN(v) ? text : formatG(v * scale);
}

// Mirrors applyOps in backend/internal/mappingengine/engine.go and the C
// engine (shared corpus: mapping-fixtures/v1/ops-*). Runs a rule's `ops` chain
// left to right over a numeric text, after cast and unitScale. undefined means
// the rule must yield NOTHING (as if its source were missing): the number or an
// operand is absent or not numeric, the operator is unknown, a division is by
// zero, or the result is not finite. There is deliberately no fallback to the
// untouched number - a wrong value is worse than none.
export function applyOps(text: string, ops: MappingOp[] | undefined, record: JsonValue): string | undefined {
  if (!Array.isArray(ops) || ops.length === 0) return text;
  let v = parseFloat(text);
  if (Number.isNaN(v)) return undefined;
  for (const o of ops) {
    let operand: number;
    if (o && typeof o.from === 'string' && o.from !== '') {
      const t = valueToText(resolvePath(record, o.from));
      if (t === undefined) return undefined;
      operand = parseFloat(t);
      if (Number.isNaN(operand)) return undefined;
    } else if (o && typeof o.value === 'number') {
      operand = o.value;
    } else {
      return undefined;
    }
    switch (o.op) {
      case '+':
        v += operand;
        break;
      case '-':
        v -= operand;
        break;
      case '*':
        v *= operand;
        break;
      case '/':
        if (operand === 0) return undefined;
        v /= operand;
        break;
      default:
        return undefined;
    }
    if (!Number.isFinite(v)) return undefined;
  }
  return formatG(v);
}

function applyMapValues(text: string, map: Record<string, JsonValue> | undefined): string {
  if (!map || typeof map !== 'object') return text;
  const mapped = valueToText(map[text]);
  return mapped !== undefined ? mapped : text; // no entry: pass through
}

// --- timestamps (mirrors parse_timestamp/parse_rfc3339_ms) --------------

function parseRfc3339Ms(text: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?(Z|z|[+-]\d{2}:\d{2})?/.exec(text);
  if (!m) return null;
  const [, y, mo, d, h, mi, s, frac, off] = m;
  const ms = frac ? parseInt((frac + '000').slice(0, 3), 10) : 0;
  let offSec = 0;
  if (off && off !== 'Z' && off !== 'z') {
    const sign = off[0] === '-' ? -1 : 1;
    const [oh, om] = off.slice(1).split(':').map(Number);
    offSec = sign * (oh * 3600 + om * 60);
  }
  return Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s), ms) - offSec * 1000;
}

// --- free-format timestamp patterns (mirrors parse_pattern_ms in C / parsePatternMs in Go) --
//
// Any `timestamp` value other than unix_s / unix_ms / rfc3339 is a pattern:
//   YYYY  4-digit year          YY  2-digit year (00-68 -> 20xx, 69-99 -> 19xx)
//   MM    month   DD day   HH hour   mm or II minute   ss second   (1-2 digits)
//   S+    fraction, 1-9 digits, truncated to milliseconds
//   Z     "Z", +HH:MM or +HHMM (optional when the text ends; no zone = UTC)
// Every other character is a literal that must match. The whole text must be
// consumed, the calendar is validated, and year+month+day tokens are required.

function isDigit(c: string | undefined): boolean {
  return c !== undefined && c >= '0' && c <= '9';
}

function takeDigits(text: string, pos: number, min: number, max: number): [number, number] | null {
  let n = 0;
  let i = pos;
  while (i < text.length && i - pos < max && isDigit(text[i])) n = n * 10 + (text.charCodeAt(i++) - 48);
  return i - pos >= min ? [n, i] : null;
}

function daysInMonth(y: number, m: number): number {
  if (m === 2) return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 29 : 28;
  return [4, 6, 9, 11].includes(m) ? 30 : 31;
}

function parsePatternMs(text: string, pattern: string): number | null {
  let y = -1, mo = -1, d = -1, h = 0, mi = 0, sec = 0, ms = 0, off = 0;
  let t = 0;
  let p = 0;
  const num = (min: number, max: number): number | null => {
    const r = takeDigits(text, t, min, max);
    if (!r) return null;
    t = r[1];
    return r[0];
  };
  while (p < pattern.length) {
    const rest = pattern.slice(p);
    let v: number | null;
    if (rest.startsWith('YYYY')) {
      if ((v = num(4, 4)) === null) return null;
      y = v;
      p += 4;
    } else if (rest.startsWith('YY')) {
      if ((v = num(2, 2)) === null) return null;
      y = v < 69 ? 2000 + v : 1900 + v;
      p += 2;
    } else if (rest.startsWith('MM')) {
      if ((mo = num(1, 2) ?? -1) < 0) return null;
      p += 2;
    } else if (rest.startsWith('DD')) {
      if ((d = num(1, 2) ?? -1) < 0) return null;
      p += 2;
    } else if (rest.startsWith('HH')) {
      if ((v = num(1, 2)) === null) return null;
      h = v;
      p += 2;
    } else if (rest.startsWith('mm') || rest.startsWith('II')) {
      if ((v = num(1, 2)) === null) return null;
      mi = v;
      p += 2;
    } else if (rest.startsWith('ss')) {
      if ((v = num(1, 2)) === null) return null;
      sec = v;
      p += 2;
    } else if (rest[0] === 'S') {
      while (pattern[p] === 'S') p++;
      const start = t;
      if (num(1, 9) === null) return null;
      ms = parseInt((text.slice(start, t) + '00').slice(0, 3), 10);
    } else if (rest[0] === 'Z') {
      p++;
      if (t === text.length) continue; // optional at end of text: UTC
      const c = text[t];
      if (c === 'Z' || c === 'z') {
        t++;
      } else if (c === '+' || c === '-') {
        t++;
        const oh = num(2, 2);
        if (oh === null) return null;
        if (text[t] === ':') t++;
        const om = num(2, 2);
        if (om === null || oh > 23 || om > 59) return null;
        off = (c === '-' ? -1 : 1) * (oh * 3600 + om * 60);
      } else {
        return null;
      }
    } else {
      if (text[t] !== rest[0]) return null;
      t++;
      p++;
    }
  }
  if (t !== text.length) return null;
  if (y < 0 || mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo)) return null;
  if (h > 23 || mi > 59 || sec > 59) return null;
  // setUTCFullYear, not Date.UTC: the latter maps years 0-99 to 19xx.
  const dt = new Date(0);
  dt.setUTCFullYear(y, mo - 1, d);
  dt.setUTCHours(h, mi, sec, ms);
  return dt.getTime() - off * 1000;
}

export function parseTimestamp(text: string, format: string | undefined): number | null {
  if (!text || !format) return null;
  if (format === 'unix_s') {
    const v = parseFloat(text);
    return Number.isNaN(v) ? null : Math.round(v * 1000);
  }
  if (format === 'unix_ms') {
    const v = parseFloat(text);
    return Number.isNaN(v) ? null : Math.round(v);
  }
  if (format === 'rfc3339') return parseRfc3339Ms(text);
  return parsePatternMs(text, format);
}

// --- one output (mirrors apply_one_output) -------------------------------

export type OutputStatus = 'emitted' | 'dropped-required' | 'no-name' | 'no-rules';

export interface OutputOutcome {
  status: OutputStatus;
  name?: string;
  // signalRef (docs/SIGNAL_TEMPLATES_AS_MAPPING_SCHEMA_PLAN.md Phase 2):
  // set when a rule targets `to: "signalRef"`, mirroring the Go/C engines'
  // identical choice to leave `name` unset in that case rather than fall
  // back to defaultName - resolving a ref into a real template signal's
  // name needs server/Redis access this browser-only preview never has
  // (see this file's header comment), so the preview can only show the
  // *unresolved* ref, not the type it would actually end up written as.
  signalRef?: string;
  value?: string;
  atMs?: number;
  labels: Record<string, string>;
}

function applyOneOutput(
  rules: MappingRule[] | undefined,
  record: JsonValue,
  defaultName: string | undefined,
): OutputOutcome {
  if (!Array.isArray(rules)) return { status: 'no-rules', labels: {} };

  let name: string | undefined;
  let signalRef: string | undefined;
  let value: string | undefined;
  let atMs: number | undefined;
  const labels: Record<string, string> = {};
  let dropped = false;

  for (const rule of rules) {
    const to = rule.to;
    if (!to) continue;

    let text: string | undefined;
    if (rule.from) text = valueToText(resolvePath(record, rule.from));
    if (text === undefined) text = valueToText(rule.const);
    if (text === undefined) text = valueToText(rule.default);

    if (text === undefined) {
      if (rule.required) dropped = true;
      continue;
    }

    text = applyMapValues(text, rule.map);
    text = applyCast(text, rule.cast);
    text = applyUnitScale(text, rule.unitScale);
    const operated = applyOps(text, rule.ops, record);
    if (operated === undefined) {
      // the ops chain could not be evaluated: the rule yields nothing
      if (rule.required) dropped = true;
      continue;
    }
    text = operated;

    if (to === 'name') {
      name = text;
    } else if (to === 'signalRef') {
      signalRef = text;
    } else if (to === 'value') {
      value = text;
    } else if (to === 'at') {
      const ms = parseTimestamp(text, rule.timestamp);
      if (ms !== null) atMs = ms;
    } else if (to.startsWith('labels.') && to.length > 7) {
      labels[to.slice(7)] = text;
    }
    // Unrecognised `to` targets are ignored, not fatal - matches the C engine.
  }

  if (dropped) return { status: 'dropped-required', labels: {} };
  if (signalRef !== undefined) {
    return { status: 'emitted', signalRef, value, atMs, labels };
  }
  const finalName = name ?? defaultName;
  if (!finalName) return { status: 'no-name', labels: {} };
  return { status: 'emitted', name: finalName, value, atMs, labels };
}

// --- whole spec (mirrors mapping_engine_apply) ---------------------------

// Always one outcome per output definition (spec.outputs[], or a single
// implicit output from spec.rules) - unlike the C function (which just
// returns a count), preview needs to show *why* an output didn't emit.
export function applyMappingSpec(
  spec: MappingSpec | undefined,
  record: JsonValue,
  defaultName: string | undefined,
): OutputOutcome[] {
  const outputDefs: MappingOutputSpec[] = Array.isArray(spec?.outputs)
    ? (spec!.outputs as MappingOutputSpec[])
    : [{ rules: spec?.rules }];
  return outputDefs.map((def) => applyOneOutput(def?.rules, record, defaultName));
}

// --- record transform (mirrors mapping_engine_transform) -------------------

function toNum(v: JsonValue | undefined): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function keyText(v: JsonValue | undefined): string {
  return v === undefined || v === null || typeof v === 'object' ? '' : String(v);
}

const isRecordObject = (v: JsonValue): v is { [key: string]: JsonValue } =>
  !!v && typeof v === 'object' && !Array.isArray(v);

function compareBy(sortBy: (string | SortSpec)[]) {
  return (a: JsonValue, b: JsonValue): number => {
    for (const key of sortBy) {
      const field = typeof key === 'string' ? key : key.field;
      const desc = typeof key !== 'string' && key.order === 'desc';
      const va = resolvePath(a, field);
      const vb = resolvePath(b, field);
      const na = toNum(va);
      const nb = toNum(vb);
      let c = na !== null && nb !== null ? Math.sign(na - nb) : keyText(va).localeCompare(keyText(vb), 'en');
      if (desc) c = -c;
      if (c !== 0) return c;
    }
    return 0;
  };
}

export function applyTransform(spec: MappingSpec | undefined, records: JsonValue[]): JsonValue[] {
  const t = spec?.transform;
  if (!t || typeof t !== 'object') return records;

  let rows: JsonValue[] = records.filter(isRecordObject);

  const groupBy = t.groupBy ?? [];
  const aggs = t.aggregate ?? [];
  if (groupBy.length > 0 || aggs.length > 0) {
    const groups = new Map<string, JsonValue[]>();
    for (const row of rows) {
      const key = groupBy.map((f) => keyText(resolvePath(row, f))).join('\u001f');
      const list = groups.get(key);
      if (list) list.push(row);
      else groups.set(key, [row]);
    }
    rows = [...groups.values()].map((members) => {
      const out: { [key: string]: JsonValue } = {};
      for (const f of groupBy) {
        const v = resolvePath(members[0], f);
        if (v !== undefined) out[f] = v;
      }
      for (const agg of aggs) {
        const name = agg.as || agg.field || 'count';
        const values = agg.field ? members.map((m) => resolvePath(m, agg.field)) : [];
        const nums = values.map(toNum).filter((n): n is number => n !== null);
        switch (agg.op) {
          case 'count':
            out[name] = members.length;
            break;
          case 'first':
            if (values[0] !== undefined) out[name] = values[0];
            break;
          case 'last':
            if (values[values.length - 1] !== undefined) out[name] = values[values.length - 1] as JsonValue;
            break;
          default:
            if (nums.length > 0) {
              const sum = nums.reduce((a, b) => a + b, 0);
              out[name] = agg.op === 'sum' ? sum : agg.op === 'avg' ? sum / nums.length : agg.op === 'min' ? Math.min(...nums) : Math.max(...nums);
            }
        }
      }
      return out;
    });
  }

  if (t.sortBy && t.sortBy.length > 0) rows = [...rows].sort(compareBy(t.sortBy)); // stable

  if (typeof t.limit === 'number' && t.limit >= 0) rows = rows.slice(0, t.limit);

  if (t.select && t.select.length > 0) {
    rows = rows.map((row) => {
      const out: { [key: string]: JsonValue } = {};
      for (const f of t.select!) {
        const v = resolvePath(row, f);
        if (v !== undefined) out[f] = v;
      }
      return out;
    });
  }
  return rows;
}
